// The drive's trees as a scannable table. Every Tree ID links to that tree's
// page. The table scrolls inside its own container so the page body never
// scrolls horizontally on a phone.

import { Link } from "react-router-dom";
import { Sprout } from "lucide-react";
import { formatDate } from "@/lib/date";

const STATUS_STYLE = {
    alive: "bg-emerald-50 text-emerald-700",
    dead: "bg-red-50 text-red-700",
    replaced: "bg-amber-50 text-amber-700",
};

function StatusPill({ value }) {
    if (!value) return <span className="text-seedh-muted">—</span>;
    const cls = STATUS_STYLE[String(value).toLowerCase()] || "bg-gray-100 text-gray-600";
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
            {value}
        </span>
    );
}

export default function DriveTreesTable({ trees = [] }) {
    if (trees.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-seedh-border bg-seedh-surface-beige/40 px-6 py-10 text-center">
                <Sprout className="w-7 h-7 text-seedh-brown mx-auto" />
                <p className="text-sm text-seedh-muted mt-3">No trees published for this drive yet.</p>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-seedh-border bg-white overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[34rem]">
                    <thead>
                        <tr className="bg-seedh-surface-beige/50 text-seedh-muted">
                            <th scope="col" className="text-left font-medium px-5 py-3 whitespace-nowrap">Tree ID</th>
                            <th scope="col" className="text-left font-medium px-5 py-3 whitespace-nowrap">Species</th>
                            <th scope="col" className="text-left font-medium px-5 py-3 whitespace-nowrap">Planted on</th>
                            <th scope="col" className="text-left font-medium px-5 py-3 whitespace-nowrap">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trees.map((t) => (
                            <tr
                                key={t.id}
                                className="border-t border-seedh-border/70 hover:bg-seedh-surface-blue/30 transition-colors"
                            >
                                <td className="px-5 py-3 whitespace-nowrap">
                                    <Link
                                        to={`/tree/${encodeURIComponent(t.id)}`}
                                        className="font-mono text-xs text-seedh-blue link-underline"
                                    >
                                        {t.id}
                                    </Link>
                                </td>
                                <td className="px-5 py-3 text-seedh-text">
                                    {t.species || <span className="text-seedh-muted">—</span>}
                                </td>
                                <td className="px-5 py-3 text-seedh-text whitespace-nowrap">
                                    {formatDate(t.date) || <span className="text-seedh-muted">—</span>}
                                </td>
                                <td className="px-5 py-3">
                                    <StatusPill value={t.status} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
